import { UniDriver, waitFor } from '@wix/wix-ui-test-utils/unidriver';
import { baseUniDriverFactory } from '../../unidriver';
import {
  EmptyStateUniDriver,
  InfoIconUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import { CustomFieldPanelUniDriver } from '../CustomFieldsPanel/CustomFieldPanel.uni.driver';
import { CustomFieldModalUniDriver } from '../CustomFieldModal/CustomFieldModal.uni.driver';
import CustomFieldsFormUniDriver from '../CustomFieldsForm/CustomFieldsForm.uni.driver';
import { ArchiveFieldModalUniDriver } from '../CustomFieldsPanel/ArchiveFieldModal.uni.driver';
import { DisabledTooltipButtonUniDriver } from '../DisabledTooltip/DisabledTooltipButton.uni.driver';
import CardContainerUniDriver from '../CardContainer/CardContainer.uni.driver';

export default (base: UniDriver, body: UniDriver) => {
  const addCustomFieldButton = () =>
    DisabledTooltipButtonUniDriver(base, body, {
      dataHook: 'add-custom-field-btn',
    });

  const manageFieldsButton = () =>
    DisabledTooltipButtonUniDriver(base, body, {
      dataHook: 'manage-fields-button',
    });

  const emptyState = () =>
    EmptyStateUniDriver(base.$(`[data-hook="empty-state"]`), body);

  const cardContainer = () => CardContainerUniDriver(base, body);

  const form = () =>
    CustomFieldsFormUniDriver(base.$(`[data-hook="custom-fields-form"]`), body);

  return {
    /** Returns title text */
    getTitle: () => base.$(`[data-hook="title"]`).text(),
    /** Returns subtitle text */
    getSubtitle: async () => {
      const infoIcon = InfoIconUniDriver(
        base.$('[data-hook="subtitle-info-icon"]'),
        body,
      );

      if (await infoIcon.exists()) {
        return infoIcon.getContent();
      }

      return base.$(`[data-hook="subtitle"]`).text();
    },
    /** Checks whether the form component rendered */
    isFormExists: () => form().exists(),
    /** Waits for form rendering */
    waitForForm: (timeout: number = 5000) =>
      waitFor(() => form().exists(), timeout),
    addCustomFieldButton,
    manageFieldsButton,
    /** Gets add field button text */
    getAddFieldContent: () => addCustomFieldButton().getButtonTextContent(),
    /** Gets manage fields button text */
    getManageFieldsContent: () => manageFieldsButton().getButtonTextContent(),
    /** Indicates whether the widget loading state shows */
    showsSkeletonHeader: () => cardContainer().showsSkeletonHeader(),

    /** Clicks on add field button */
    clickAddField: () => addCustomFieldButton().click(),
    /** Checks whether the add field button rendered */
    addCustomFieldButtonExists: () => addCustomFieldButton().exists(),
    /** Clicks on manage fields button */
    clickManageFieldsContent: () => manageFieldsButton().click(),
    /** Checks whether the manage fields button rendered */
    manageCustomFieldsButtonExists: () => manageFieldsButton().exists(),
    /** Returns the error state title */
    getErrorTitle: () => cardContainer().getErrorState().getTitleText(),
    /** Returns the error state subtitle */
    getErrorSubtitle: () => cardContainer().getErrorState().getSubtitleText(),
    /** Returns the empty state title */
    getEmptyTitle: () => emptyState().getTitleText(),
    /** Returns the empty state subtitle */
    getEmptySubtitle: () => emptyState().getSubtitleText(),
    /** Clicks on try again button when error state visible */
    clickTryAgain: () =>
      cardContainer().getErrorState().getRetryButton().click(),
    /** Gets try again button text */
    getTryAgainContent: () =>
      cardContainer().getErrorState().getRetryButton().getButtonTextContent(),
    /** Returns custom fields side panel driver */
    sidePanel: () =>
      CustomFieldPanelUniDriver(
        body.$(`[data-hook="cairo-modals-container"]`),
        body,
      ),
    /** Returns custom field modal driver */
    customFieldModal: () => CustomFieldModalUniDriver(base, body),
    /** Returns archive field modal driver */
    archiveFieldModal: () => ArchiveFieldModalUniDriver(base, body),
    /** Returns field driver by given ID */
    fieldById: (id: string) => form().field(id),
    ...baseUniDriverFactory(base),
  };
};
